import React, { useState, useEffect, useRef } from 'react'
import { MenuProps } from 'components/menu'
import FloatingMenu from 'components/menu/floating'
import { MenuHeaderAction } from 'components/menu/items'
import { ITableColumn, ITableViewColour } from 'types'
import { IFilterType, IColumnTypes, ISelectOption } from 'components/spreadsheet/types'
import ColourItem from 'components/spreadsheet/components/menu/views/colour/ColourItem'
import { getOriginalColumns } from 'components/spreadsheet/helpers/functions'
import { areColourSettingsEqual } from 'components/spreadsheet/helpers/colouring'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { PanelAdd, PanelRules } from 'components/menu/panel'
import IconSelector from 'components/spreadsheet/components/cell/components/icon'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { getDefaultFilterTypeForColType } from 'components/spreadsheet/helpers/filtering'

export interface ISelectAndKindOption extends ISelectOption {
  kind: IColumnTypes
}
export interface IColourItemProps extends ITableViewColour {
  selectedColumns: ISelectOption[]
  kind: IColumnTypes
  column: ITableColumn
  columnOptions: ISelectAndKindOption[]
  focus: boolean
}

/** The rules with the one at `from` taken out and put back at `to`: a move, not a swap. */
export const moveRule = <T,>(rules: T[], from: number, to: number): T[] => {
  const moved = [...rules]
  const [rule] = moved.splice(from, 1)
  moved.splice(to, 0, rule)
  return moved
}

const ColourMenu: React.FC<MenuProps> = ({ id, menuState, setMenuState, width }) => {
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const allColumns = getOriginalColumns(spreadsheetData)

  const columnOptions: ISelectAndKindOption[] = allColumns.map((column) => ({
    label: column.name,
    value: column.publicId,
    kind: column.kind
  }))

  const areEqualSettings = (currentColour: ITableViewColour, newColour: ITableViewColour) => {
    const currentMultipleValues = currentColour.multipleValues || []
    const newMultipleValues = newColour.multipleValues || []
    return (
      currentColour.value === newColour.value &&
      JSON.stringify(currentMultipleValues) === JSON.stringify(newMultipleValues) &&
      currentColour.filterType === newColour.filterType &&
      currentColour.backgroundColour === newColour.backgroundColour &&
      currentColour.fontColour === newColour.fontColour &&
      currentColour.fontBold === newColour.fontBold &&
      currentColour.fontItalic === newColour.fontItalic &&
      currentColour.fontUnderline === newColour.fontUnderline &&
      currentColour.applyToEntireRow === newColour.applyToEntireRow
    )
  }
  const [colourSettings, setColourSettings] = useState<IColourItemProps[]>(() => {
    const groups: IColourItemProps[] = []
    const colours = spreadsheetData.userConfiguration.colourSettings
    const keys = new Set()
    colours.forEach((colour, index) => {
      if (index === 0) {
        const column = allColumns.find((c) => c.publicId === colour.columnId)
        if (!column) return
        keys.add(
          `${colour.columnId}-${colour.filterType}-${colour.value}-${colour.backgroundColour}-${JSON.stringify(
            colour.multipleValues
          )}`
        )
        groups.push({
          ...colour,
          kind: column.kind,
          selectedColumns: [{ value: colour.columnId, label: colour.columnName }],
          column,
          columnOptions,
          focus: false
        })
      } else {
        colours.slice(index).forEach((colour) => {
          const key = `${colour.columnId}-${colour.filterType}-${colour.value}-${
            colour.backgroundColour
          }-${JSON.stringify(colour.multipleValues)}`
          if (!keys.has(key)) {
            keys.add(key)
            const findColourSetting = groups.find((group) => areEqualSettings(group, colour))
            if (findColourSetting) {
              findColourSetting.selectedColumns.push({ value: colour.columnId, label: colour.columnName })
            } else {
              const innerColumn = allColumns.find((c) => c.publicId === colour.columnId)
              if (!innerColumn) return
              groups.push({
                ...colour,
                kind: innerColumn.kind,
                selectedColumns: [{ value: colour.columnId, label: colour.columnName }],
                column: innerColumn,
                columnOptions,
                focus: false
              })
            }
          }
        })
      }
    })
    return groups
  })

  // When there are changes to filters, propagate changes to main state
  const timeoutRef = useRef<TimeoutID | null>(null)
  useEffect(() => {
    const hasChanged = colourSettings !== spreadsheetData.userConfiguration.colourSettings
    if (spreadsheetData.loading || !hasChanged) {
      return
    }
    if (timeoutRef.current) {
      cancelTimeout(timeoutRef.current)
    }

    const keys = new Set()
    let newColourSettings = colourSettings.flatMap((colour) => {
      return colour.selectedColumns.map((col) => {
        const key = `${col.value}-${colour.filterType}-${colour.value}-${colour.backgroundColour}-${JSON.stringify(
          colour.multipleValues
        )}`
        if (keys.has(key)) {
          return undefined
        } else {
          keys.add(key)
          const isMultiple = colour.filterType === IFilterType.one_of || colour.filterType === IFilterType.not_one_of
          const newColourSetting: ITableViewColour = {
            columnId: col.value,
            columnName: col.label,
            filterType: colour.filterType,
            value: isMultiple ? '' : colour.value || '',
            multipleValues: isMultiple ? colour.multipleValues || [] : [],
            backgroundColour: colour.backgroundColour,
            fontColour: colour.fontColour,
            fontBold: colour.fontBold,
            fontItalic: colour.fontItalic,
            fontUnderline: colour.fontUnderline,
            applyToEntireRow: colour.applyToEntireRow
          }
          return newColourSetting
        }
      })
    })
    newColourSettings = newColourSettings.filter((c) => c !== undefined)
    const rebuiltColourSettings = newColourSettings as ITableViewColour[]
    if (areColourSettingsEqual(rebuiltColourSettings, spreadsheetData.userConfiguration.colourSettings)) {
      return
    }
    // The view it's made in: should the author have switched view by the time it saves, it's dropped.
    const viewId = spreadsheetData.viewDetails.publicId
    timeoutRef.current = requestTimeout(() => {
      setSpreadsheetData({
        type: 'UPDATE_COLOURS',
        colours: rebuiltColourSettings,
        viewId
      })
    }, 1000)
  }, [colourSettings, spreadsheetData.loading])

  const handleDeleteColour = (index: number) => {
    setColourSettings(colourSettings.filter((_, i) => i !== index))
  }

  const handleColourChange = (newColour: IColourItemProps, prevColour: IColourItemProps) => {
    const newColourSettings: IColourItemProps[] = []
    colourSettings.forEach((colour) => {
      if (areEqualSettings(prevColour, colour)) {
        newColourSettings.push({ ...newColour, focus: prevColour.value !== newColour.value })
      } else {
        newColourSettings.push(colour)
      }
    })
    setColourSettings(newColourSettings)
  }
  const clearFocus = (arr: IColourItemProps[]) => {
    return arr.map((c) => ({ ...c, focus: false }))
  }

  const handleOrderChange = (from: number, to: number) => {
    if (from === to) return
    setColourSettings(moveRule(clearFocus(colourSettings), from, to))
  }

  const handleAddColour = (option: string, filterType?: IFilterType) => {
    const columnName = columnOptions.find((col) => col.value === option)?.label ?? ''
    const matchedColumn = allColumns.find((c) => c.publicId === option)
    if (!matchedColumn) return

    const newColour: ITableViewColour = {
      columnId: option,
      columnName: columnName,
      filterType: filterType ? filterType : getDefaultFilterTypeForColType(matchedColumn.kind),
      value: '',
      multipleValues: [],
      backgroundColour: '#ffffff',
      fontColour: '#333333',
      fontBold: false,
      fontItalic: false,
      fontUnderline: false,
      applyToEntireRow: false
    }

    let found = false
    const newColourSettings = clearFocus(colourSettings).map((colour) => {
      if (areEqualSettings(colour, newColour)) {
        found = true
        const newColumns = [
          ...colour.selectedColumns.filter((col) => col.value !== newColour.columnId),
          { value: newColour.columnId, label: newColour.columnName }
        ].sort((a, b) => a.label.localeCompare(b.label))

        return {
          ...colour,
          selectedColumns: newColumns,
          focus: true
        }
      }
      return colour
    })
    if (!found) {
      const column = allColumns.find((c) => c.publicId === option)
      if (!column) return
      newColourSettings.push({
        ...newColour,
        kind: column.kind,
        column,
        columnOptions,
        selectedColumns: [{ value: newColour.columnId, label: newColour.columnName }],
        focus: true
      })
    }
    setColourSettings(newColourSettings)
  }

  useEffect(() => {
    if (menuState.defaultColour) {
      handleAddColour(menuState.defaultColour.columnId, menuState.defaultColour.filterType)
    }
  }, [])

  return (
    <FloatingMenu
      id={id}
      menuState={menuState}
      setMenuState={setMenuState}
      width={width}
      zIndex={3000}
      title="Conditional formatting"
      action={
        colourSettings.length > 0 && (
          <MenuHeaderAction onClick={() => setColourSettings([])}>Clear all</MenuHeaderAction>
        )
      }
    >
      {colourSettings.length > 0 && (
        // The first rule a cell meets is the one it takes, so the order matters and can be dragged.
        <PanelRules ids={colourSettings.map((_, index) => `colour-${index}`)} onReorder={handleOrderChange}>
          {colourSettings.map((colourSetting, index) => (
            <ColourItem
              key={`colour-item-${index}`}
              {...colourSetting}
              onDelete={handleDeleteColour}
              onChange={handleColourChange}
              index={index}
              optionsOpen={false}
            />
          ))}
        </PanelRules>
      )}
      <PanelAdd
        data-tour="add-colour"
        label="Add rule"
        options={columnOptions.map((option) => ({ ...option, icon: <IconSelector kind={option.kind} /> }))}
        onPick={(option) => handleAddColour(option)}
      />
    </FloatingMenu>
  )
}

export default ColourMenu
